import { useHistory, useRouteMatch } from "react-router-dom";
import Signals from "features/signals/Signals";
import SignalActionsProvider from "features/signals/SignalActionsProvider";
import FollowSignalProvider from "@shared/features/signals/FollowSignalProvider";
import SignalFollowModal from "features/signals/SignalFollow/SignalFollowModal";
import useMedia from "utils/hooks/useMedia";
import {
  ADMIN_SIGNALS_PATH,
  CREATE_SIGNAL_PATH,
  MOBILE_SEARCH,
} from "common/constants";
import styled, { useTheme } from "styled-components";
import { Download, Plus } from "lucide-react";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook/layout.ts";
import NewLookSignalsIntro from "features/signals/NewLook/NewLookSignalsIntro.tsx";
import { NewLookPageBody } from "features/portfolio/NewLook/styles.ts";
import NewLookFiltersBar from "features/portfolio/NewLook/NewLookFiltersBar.tsx";
import {
  SignalsStatus,
  SignalsStatusValueType,
} from "@shared/features/signals/common/types";
import { getSignalsFiltersSlice } from "@shared/features/signals/common/utils";
import { displayResultMessage } from "utils/misc";
import { sharedLib } from "@shared/services/shared.service";
import Flex from "common/Boxes/Flex";
import ExportOutlined from "@ant-design/icons/ExportOutlined";
import FilterOutlined from "@ant-design/icons/FilterOutlined";
import Button from "common/Buttons/Button";
import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useBottomNavigator, useDisablePageScroll } from "utils/hooks/misc";
import FiltersChips from "common/Filters/FiltersChips/FiltersChips";
import { FilterType, LanguageNameSpaces } from "@shared/types";
import StickyHeader from "common/Headers/StickyHeader";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import FiltersCounter from "common/Filters/common/FiltersCounter";
import ExpandCollapse from "common/Animations/ExpandCollapse";
import { useSelector } from "react-redux";
import usePageWrapper from "common/Home/PageWrapper/usePageWrapper.ts";
import { SignalActionsContext } from "@shared/features/signals/common/SignalActions.context";
import BulkActions from "common/Table/BulkActions";
import GroupSelect from "features/admin/group/GroupSelect.tsx";
import PlusOutlined from "@ant-design/icons/PlusOutlined";
import BuyButton from "common/Buttons/BuyButton.tsx";
import PageTabs from "common/Tabs/PageTabs.tsx";
import Tabs from "common/Tabs/Tabs";
import { IS_TELEGRAM_WEB_APP } from "features/telegram/constants";
import {
  SIGNALS_PAGE_PADDING_BOTTOM,
  SIGNALS_PAGE_TABS_ROW_HEIGHT,
} from "features/signals/common/constants.ts";
import SignalsPageProvider from "features/signals/SignalsPageProvider.tsx";
import { SignalsPageContext } from "@shared/features/signals/common/SignalsPage.context";
import { useSignalsQuery } from "features/signals/common/hooks.tsx";

const SignalsPage = () => {
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  useDisablePageScroll(isBottomNavigator);
  const { t, i18n } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  const isAdminSignalsPage = Boolean(useRouteMatch(ADMIN_SIGNALS_PATH));
  const { signalsStatus } = useSignalsQuery(isAdminSignalsPage);
  const urlIsOpen = signalsStatus === SignalsStatus.OPEN;

  const translatedTabsItems = useMemo(
    () => TABS_ITEMS.map((item) => ({ ...item, label: t(item.label) })),
    [i18n.language]
  );

  const getContent = useCallback(
    (isOpen: boolean) => {
      return (
        <SignalsPageProvider isOpen={isOpen}>
          <SignalActionsProvider>
            <FollowSignalProvider SignalFollowModal={SignalFollowModal}>
              <InnerSignalsPage
                isOpen={isOpen}
                tabsItems={translatedTabsItems}
              />
            </FollowSignalProvider>
          </SignalActionsProvider>
        </SignalsPageProvider>
      );
    },
    [translatedTabsItems]
  );

  const mobileTabItems = useMemo(() => {
    if (!isBottomNavigator) return [];
    return translatedTabsItems.map((item) => ({
      ...item,
      children: getContent(item.key === SignalsStatus.OPEN),
    }));
  }, [isBottomNavigator, getContent]);

  if (isBottomNavigator) {
    return <PageTabs items={mobileTabItems} />;
  }

  return getContent(urlIsOpen);
};

export default SignalsPage;

interface InnerSignalsPageProps {
  isOpen: boolean;
  tabsItems: { key: string; label: string }[];
}

const InnerSignalsPage = ({ isOpen, tabsItems }: InnerSignalsPageProps) => {
  const { t, i18n } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  const { isNewLook } = useTheme();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const history = useHistory();
  const isMobileFilters = useMedia([`(max-width: 1350px)`], [true], false);
  const showFiltersButton = useMedia([`(max-width: 550px)`], [false], true);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const isAssetManager = user?.isAssetManager();

  const { subGroupId, setSignalStatus, isAdminSignals } =
    useContext(SignalsPageContext)!;

  const filtersSlice = getSignalsFiltersSlice(isOpen, isAdminSignals);
  const numActiveFilters = useSelector(
    filtersSlice.selectors.selectNumActiveFilters
  );
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const {
    selectedSignalIDs,
    setSelectedSignalIDs,
    exitBulkMode,
    actionButtonsData,
    selectAll,
    signalIDs,
    showEditSignalActions,
  } = useContext(SignalActionsContext)!;

  const bulkActionsBar =
    showEditSignalActions && selectedSignalIDs.length > 0 ? (
      <BulkActions
        actionButtonsData={actionButtonsData}
        unselectAll={() => setSelectedSignalIDs([])}
        selectAll={() => selectAll(signalIDs ?? [])}
        numSelected={selectedSignalIDs.length}
        onCancel={exitBulkMode}
      />
    ) : undefined;

  const showCreateSignalButton = isAssetManager || isAdminSignals;

  usePageWrapper({
    filters: {
      selectors: filtersSlice?.selectors,
      actions: filtersSlice?.actions,
    },
    showSearch: true,
    hideMobileFilters: true,
    dependencies: [isOpen],
  });

  useEffect(() => {
    if (!showFiltersButton) {
      setFiltersExpanded(true);
    }
  }, [showFiltersButton]);

  const [exportSignals, exportSignalsStatus] =
    sharedLib.sharedApi.signals.useLazyExportSignalsQuery();

  const onExportClick = () => {
    if (!subGroupId) return;
    displayResultMessage(
      exportSignals({
        open_signals: isOpen,
        closed_signals: !isOpen,
        sub_group_id: subGroupId,
      }).unwrap()
    )
      .then((downloadLink) => {
        if (downloadLink) {
          window.location.href = downloadLink;
        }
      })
      .catch(() => {});
  };

  const getFiltersChips = useCallback(() => {
    const filtersSlice = getSignalsFiltersSlice(isOpen, isAdminSignals);
    const extraComponents: ReactNode[] = [];
    if (isAdminSignals && isBottomNavigator) {
      extraComponents.push(<GroupSelect key="groupSelect" size="middle" />);
    }
    return (
      <div style={{ padding: isBottomNavigator ? "" : "0 16px 16px" }}>
        {isNewLook ? (
          <NewLookFiltersBar
            filtersActions={filtersSlice.actions}
            filtersSelectors={filtersSlice.selectors}
            hideFilterTypes={isAdminSignals ? [FilterType.GROUPS] : []}
            extraComponent={extraComponents}
          />
        ) : (
          <FiltersChips
            filtersActions={filtersSlice.actions}
            filtersSelectors={filtersSlice.selectors}
            hideFilterTypes={isAdminSignals ? [FilterType.GROUPS] : []}
            extraComponent={extraComponents}
          />
        )}
      </div>
    );
  }, [isAdminSignals, isBottomNavigator, isNewLook]);

  const exportButton = useMemo(() => {
    return isAdminSignals && !IS_TELEGRAM_WEB_APP ? (
      <Flex
        $justifyContent="flex-end"
        style={{ marginBottom: isBottomNavigator ? 12 : 0 }}
      >
        <Button
          $type="secondary"
          onClick={onExportClick}
          loading={exportSignalsStatus.isLoading}
        >
          <ExportOutlined />
          {t("page.export")}
        </Button>
      </Flex>
    ) : (
      <></>
    );
  }, [
    isAdminSignals,
    isBottomNavigator,
    exportSignalsStatus.isLoading,
    isOpen,
    subGroupId,
  ]);

  const onCreateClick = () => history.push(CREATE_SIGNAL_PATH);
  const onExportClickRef = useRef(onExportClick);
  onExportClickRef.current = onExportClick;

  const signalsContent = (
    <Signals
      isOpen={isOpen}
      isMobileFilters={isMobileFilters}
      renderTopContainer={() => {
        return exportButton;
      }}
    />
  );

  if (isBottomNavigator) {
    return (
      <MobileWrapper $active={true}>
        {getFiltersChips()}
        {signalsContent}
        {showCreateSignalButton && !bulkActionsBar && (
          <BuyButton onClick={onCreateClick} keepOnNewLookPhone={true} />
        )}
      </MobileWrapper>
    );
  }

  if (isNewLook) {
    const showExport = isAdminSignals && !IS_TELEGRAM_WEB_APP;
    return (
      <>
        <NewLookSignalsIntro
          activeKey={isOpen ? SignalsStatus.OPEN : SignalsStatus.CLOSED}
          tabsItems={tabsItems}
          onTabChange={(key) => setSignalStatus(key as SignalsStatusValueType)}
          actions={
            showExport || showCreateSignalButton ? (
              <>
                {showExport && (
                  <Button
                    $type="secondary"
                    onClick={() => onExportClickRef.current()}
                    loading={exportSignalsStatus.isLoading}
                  >
                    {!exportSignalsStatus.isLoading && (
                      <Download size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />
                    )}
                    {t("page.export")}
                  </Button>
                )}
                {showCreateSignalButton && (
                  <Button type="primary" onClick={onCreateClick}>
                    <Plus size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />
                    {t("page.create_signal")}
                  </Button>
                )}
              </>
            ) : undefined
          }
          deps={[
            showExport,
            showCreateSignalButton,
            exportSignalsStatus.isLoading,
            i18n.language,
          ]}
        />
        <NewLookPageBody>
          <NewLookFiltersBar
            filtersActions={filtersSlice.actions}
            filtersSelectors={filtersSlice.selectors}
            hideFilterTypes={isAdminSignals ? [FilterType.GROUPS] : []}
          />
          {bulkActionsBar}
          {signalsContent}
        </NewLookPageBody>
      </>
    );
  }

  return (
    <>
      <StickyHeader
        style={{ width: "100%", minHeight: SIGNALS_PAGE_TABS_ROW_HEIGHT }}
      >
        <Flex style={{ padding: "12px 16px" }}>
          <StyledTabs
            activeKey={isOpen ? SignalsStatus.OPEN : SignalsStatus.CLOSED}
            onChange={(key: string) =>
              setSignalStatus(key as SignalsStatusValueType)
            }
            items={tabsItems}
          />
          <Flex style={{ columnGap: 12, marginLeft: "auto" }}>
            {showFiltersButton && isMobileFilters && (
              <ButtonWrapper>
                <Button onClick={() => setFiltersExpanded(!filtersExpanded)}>
                  <FilterOutlined />
                  {t("page.filters")}
                </Button>
                <FiltersCounter numActiveFilters={numActiveFilters} />
              </ButtonWrapper>
            )}
            {exportButton}
            {showCreateSignalButton && (
              <Button type="primary" onClick={onCreateClick}>
                <PlusOutlined />
                {t("page.create_signal")}
              </Button>
            )}
          </Flex>
        </Flex>
        <ExpandCollapse expanded={filtersExpanded && !!isMobileFilters}>
          {getFiltersChips()}
        </ExpandCollapse>
        {bulkActionsBar && (
          <div style={{ padding: "0 16px 16px" }}>{bulkActionsBar}</div>
        )}
      </StickyHeader>
      <Wrapper $isMobile={isMobile}>{signalsContent}</Wrapper>
    </>
  );
};

const TABS_ITEMS = [
  {
    key: SignalsStatus.OPEN,
    label: "tabs.open",
  },
  {
    key: SignalsStatus.CLOSED,
    label: "tabs.closed",
  },
] as const;

const Wrapper = styled.div<{ $isMobile: boolean }>`
  min-height: inherit;
  height: 100%;
  padding: 0 16px 16px ${SIGNALS_PAGE_PADDING_BOTTOM}px;
  ${(p) =>
    p.$isMobile &&
    `
    display: flex;
    flex: 1;
    flex-direction: column;
  `}
`;
const MobileWrapper = styled.div<{ $active?: boolean }>`
  height: 100%;
  display: ${(p) => (p.$active ? "flex" : "none")};
  flex-direction: column;
`;
const ButtonWrapper = styled.div`
  position: relative;
`;
const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 0;
  }
  .ant-tabs-tab {
    padding-top: 0;
  }
  margin-right: 20px;
`;
